<template>
  <div class="calc-settings-panel">
    <h3 class="section-title">{{ title }}</h3>

    <div class="calc-mode-group">
      <label class="calc-mode-option">
        <input type="radio" :value="true" v-model="useNormalCalculationSettings" />
        <span>沿用常態給點規則</span>
      </label>
      <label class="calc-mode-option">
        <input type="radio" :value="false" v-model="useNormalCalculationSettings" />
        <span>自訂給點規則</span>
      </label>
    </div>

    <div v-if="useNormalCalculationSettings" class="normal-rule-summary">
      <div class="normal-rule-summary-title">
        <i class="ri-link"></i>
        將沿用常態給點規則的計算設定
      </div>
      <div v-if="normalRuleLoading" class="normal-rule-summary-hint">載入常態規則中...</div>
      <div v-else-if="!normalRule" class="normal-rule-summary-hint">
        尚未設定常態給點規則，請先前往
        <router-link to="/client/points/earning-rules/normal">常態給點規則設定</router-link>
      </div>
      <div v-else class="normal-rule-summary-items">
        <span>金額基數：每 {{ normalRule.baseAmount || 1 }} 元</span>
        <span>捨入方式：{{ normalRoundingLabel }}</span>
        <span>生效天數：{{ normalRule.effectiveDays || 0 }} 天</span>
        <span
          class="effective-mode-badge"
          :class="(normalRule.effectiveDays || 0) > 0 ? 'effective-mode-delayed' : 'effective-mode-immediate'"
        >
          {{ (normalRule.effectiveDays || 0) > 0 ? '延後生效' : '立即生效' }}
        </span>
      </div>
      <router-link to="/client/points/earning-rules/normal" class="btn btn-sm btn-secondary">
        前往常態給點規則設定
      </router-link>
    </div>

    <div v-else class="custom-calc-settings">
      <div class="calc-preview-box">
        <div class="calc-preview-title"><i class="ri-calculator-line"></i> 計算公式預覽</div>
        <div class="calc-preview-steps">
          <div class="calc-step">
            <span class="calc-step-num">1</span>
            <span class="calc-step-text">
              消費金額 ÷ <strong>{{ customSettings.baseAmount }}</strong> = 單位數
              <span class="calc-arrow">→</span>
              依捨入方式取整
            </span>
          </div>
          <div class="calc-step">
            <span class="calc-step-num">2</span>
            <span class="calc-step-text">
              單位數 × 各等級回饋率 = 點數
              <span class="calc-arrow">→</span>
              再次依捨入方式取整
            </span>
          </div>
        </div>
        <div class="calc-example">
          例：消費 180 元，基數 {{ calcPreview.baseAmount }}，回饋率 2.5，{{ customRoundingLabel }}<br/>
          → 第一段 180 ÷ {{ calcPreview.baseAmount }} = {{ calcPreview.rawUnits }} → <strong>{{ calcPreview.units }} 單位</strong><br/>
          → 第二段 {{ calcPreview.units }} × 2.5 = {{ calcPreview.rawPoints }} → <strong>{{ calcPreview.points }} 點</strong>
        </div>
      </div>

      <div class="form-group">
        <label class="form-label">金額計算基數（元）</label>
        <div class="base-amount-row">
          <div class="base-amount-presets">
            <button
              v-for="preset in [1, 10, 50, 100]"
              :key="preset"
              type="button"
              class="base-amount-btn"
              :class="{ active: customSettings.baseAmount === preset }"
              @click="customSettings.baseAmount = preset"
            >
              每 {{ preset }} 元
            </button>
          </div>
          <div class="base-amount-custom">
            <input
              v-model.number="customSettings.baseAmount"
              type="number"
              class="form-control"
              min="1"
              step="1"
              placeholder="自訂"
            />
            <span class="input-suffix">元</span>
          </div>
        </div>
        <small class="form-text">消費金額除以此基數後，再套用捨入方式得到單位數</small>
        <div v-if="errors.baseAmount" class="invalid-feedback d-block">{{ errors.baseAmount }}</div>
      </div>

      <div class="form-group">
        <label class="form-label">計算基準</label>
        <select v-model="customSettings.calculationBase" class="form-control" disabled>
          <option value="paid_amount">實付金額（扣除優惠券、折扣後）</option>
        </select>
        <small class="form-text">目前僅支援實付金額計算</small>
      </div>

      <div class="form-group">
        <label class="form-label">捨入方式（兩段均適用）</label>
        <select v-model="customSettings.roundingMethod" class="form-control">
          <option value="round">四捨五入</option>
          <option value="ceil">無條件進位</option>
          <option value="floor">無條件捨去</option>
        </select>
        <small class="form-text">第一段（金額÷基數）與第二段（單位×回饋率）均使用此方式取整</small>
      </div>

      <div class="form-group">
        <label class="form-label">生效天數</label>
        <input
          v-model.number="customSettings.effectiveDays"
          type="number"
          class="form-control"
          :class="{ 'is-invalid': errors.effectiveDays }"
          placeholder="0"
          min="0"
        />
        <div v-if="errors.effectiveDays" class="invalid-feedback">{{ errors.effectiveDays }}</div>
        <small class="form-text">0 = 立即生效，N = 購買後 N 天生效</small>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';

export default {
  name: 'TimeRangeCalcSettingsPanel',
  props: {
    title: {
      type: String,
      default: '給點計算規則'
    },
    useNormalCalculationSettings: {
      type: Boolean,
      default: true
    },
    customSettings: {
      type: Object,
      required: true
    },
    normalRule: {
      type: Object,
      default: null
    },
    normalRuleLoading: {
      type: Boolean,
      default: false
    },
    errors: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['update:useNormalCalculationSettings'],
  setup(props, { emit }) {
    const useNormalCalculationSettings = computed({
      get: () => props.useNormalCalculationSettings,
      set: (value) => emit('update:useNormalCalculationSettings', value)
    });

    const normalRoundingLabel = computed(() => {
      const map = { round: '四捨五入', ceil: '無條件進位', floor: '無條件捨去' };
      const method = props.normalRule?.roundingMethod || 'floor';
      return map[method] || '無條件捨去';
    });

    const customRoundingLabel = computed(() => {
      const map = { round: '四捨五入', ceil: '無條件進位', floor: '無條件捨去' };
      return map[props.customSettings.roundingMethod] || '無條件捨去';
    });

    const applyRounding = (value, method) => {
      if (!isFinite(value) || isNaN(value)) return 0;
      switch (method) {
        case 'ceil': return Math.ceil(value);
        case 'round': return Math.round(value);
        case 'floor':
        default: return Math.floor(value);
      }
    };

    const calcPreview = computed(() => {
      const base = (props.customSettings.baseAmount > 0) ? props.customSettings.baseAmount : 1;
      const method = props.customSettings.roundingMethod || 'floor';
      const rawUnits = 180 / base;
      const units = applyRounding(rawUnits, method);
      const rawPoints = units * 2.5;
      const points = applyRounding(rawPoints, method);
      return {
        baseAmount: base,
        rawUnits: rawUnits.toFixed(2),
        units,
        rawPoints: rawPoints.toFixed(2),
        points
      };
    });

    return {
      useNormalCalculationSettings,
      normalRoundingLabel,
      customRoundingLabel,
      calcPreview
    };
  }
};
</script>

<style scoped>
.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #333;
}

.calc-mode-group {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
}

.calc-mode-option {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  cursor: pointer;
}

.normal-rule-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  background: #f8faff;
  border: 1px solid #d8e4f8;
  border-radius: 8px;
  padding: 12px 16px;
}

.normal-rule-summary-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #2a5bd7;
}

.normal-rule-summary-items {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  color: #334155;
  font-size: 13px;
  flex: 1;
}

.normal-rule-summary-hint {
  flex: 1;
  font-size: 13px;
  color: #64748b;
}

.effective-mode-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: 600;
}

.effective-mode-immediate {
  background: #dcfce7;
  color: #166534;
}

.effective-mode-delayed {
  background: #ffedd5;
  color: #9a3412;
}

.calc-preview-box {
  background: #f0f4ff;
  border: 1px solid #c8d8f8;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 24px;
}

.calc-preview-title {
  font-size: 13px;
  font-weight: 600;
  color: #4a6fa5;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.calc-preview-steps {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.calc-step {
  display: flex;
  align-items: center;
  gap: 10px;
}

.calc-step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  background: #4a90d9;
  color: #fff;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

.calc-step-text {
  font-size: 13px;
  color: #333;
}

.calc-arrow {
  margin: 0 4px;
  color: #4a90d9;
  font-weight: 600;
}

.calc-example {
  font-size: 12px;
  color: #555;
  background: #fff;
  border-radius: 6px;
  padding: 8px 12px;
  border: 1px solid #dce8fa;
  line-height: 1.7;
}

.base-amount-row {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 420px;
}

.base-amount-presets {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.base-amount-btn {
  padding: 6px 14px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: #fff;
  color: #495057;
  font-size: 14px;
  cursor: pointer;
}

.base-amount-btn.active {
  background: #4a90d9;
  border-color: #4a90d9;
  color: #fff;
  font-weight: 600;
}

.base-amount-custom {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 180px;
}

.input-suffix {
  white-space: nowrap;
  color: #666;
  font-size: 14px;
}
</style>
